'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePermissions } from '@/hooks/usePermissions';
import type { Permission } from '@/lib/permissions';
import NotificationBell from '@/components/Projects/NotificationBell';

interface HeaderProps {
  locale: string;
  user: { email: string; id?: string } | null;
  userRole: string | null;
  locales: readonly string[];
}

export default function Header({ locale, user, userRole, locales }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const internalDropdownRef = useRef<HTMLDivElement>(null);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  // Use Supabase-based permissions (with userId for deny support)
  const { hasPermission, hasAnyPermission, isLoading: permissionsLoading } = usePermissions(userRole, user?.id);

  // Check permissions from Supabase
  const hasInternalAccess = hasAnyPermission(['hr_portal', 'customer_care', 'admin_panel', 'vet_portal', 'workbench', 'system_status', 'accounting'] as Permission[]);
  const canAccessHR = hasPermission('hr_portal');
  const canAccessHRAdmin = hasPermission('hr_admin');
  const canAccessAdmin = hasPermission('admin_panel');
  const canAccessCustomerCare = hasPermission('customer_care');
  const canAccessVet = hasPermission('vet_portal');
  const canAccessStrapi = hasPermission('strapi_access');
  const canAccessWorkbench = hasPermission('workbench');
  const canAccessSystemStatus = hasPermission('system_status');
  const canAccessAccounting = hasPermission('accounting');

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (internalDropdownRef.current && !internalDropdownRef.current.contains(event.target as Node)) {
        setInternalDropdownOpen(false);
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Primary nav links based on permissions (employee modules)
  const navLinks = [
    { href: `/${locale}/home`, label: 'Home', icon: 'fa-grid-2', show: true },
    { href: `/${locale}/customer-care`, label: 'Customer Care', icon: 'fa-headset', show: canAccessCustomerCare },
    { href: `/${locale}/accounting`, label: 'Buchhaltung', icon: 'fa-receipt', show: canAccessAccounting },
    { href: `/${locale}/hr`, label: 'HR Portal', icon: 'fa-clock', show: canAccessHR },
    { href: `/${locale}/workbench`, label: 'WorkBench', icon: 'fa-briefcase', show: canAccessWorkbench },
  ].filter(link => link.show);

  // Secondary links in "More" dropdown
  const moreLinks = [
    ...(canAccessHRAdmin ? [{ href: `/${locale}/admin/hr`, label: 'HR Admin', icon: 'fa-users-gear' }] : []),
    ...(canAccessAdmin ? [{ href: `/${locale}/pim`, label: 'PIM', icon: 'fa-cubes' }] : []),
    ...(canAccessSystemStatus ? [{ href: `/${locale}/admin/system-status`, label: 'IT System Status', icon: 'fa-server' }] : []),
    ...(canAccessStrapi ? [{ href: `/${locale}/strapi-explorer`, label: 'Strapi API Explorer', icon: 'fa-database' }] : []),
    ...(canAccessVet ? [{ href: `/${locale}/vet`, label: 'Vet Portal', icon: 'fa-user-doctor' }] : []),
  ];

  return (
    <>
      {/* Green accent line at top */}
      <div className="w-full h-1 bg-interactive-primary" />

      {/* Desktop & Tablet Header */}
      <header className="w-full bg-surface-canvas-3 py-md px-lg lg:px-2xl">
        <div className="max-w-[1440px] mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link href={`/${locale}`} className="flex-shrink-0">
            <span className="font-faro text-2xl font-bold text-content-brand">futalis</span>
          </Link>

          {/* Desktop Navigation - Employee modules */}
          <nav className="hidden lg:flex items-center gap-xl">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="font-archivo text-base text-content-brand hover:text-content-primary transition-colors flex items-center gap-xs"
              >
                <i className={`fa-solid ${link.icon} text-sm`} />
                {link.label}
              </Link>
            ))}

            {/* More dropdown for secondary modules */}
            {moreLinks.length > 0 && (
              <div className="relative" ref={internalDropdownRef}>
                <button
                  onClick={() => setInternalDropdownOpen(!internalDropdownOpen)}
                  className="font-archivo text-base text-content-brand flex items-center gap-xs hover:opacity-80 transition-opacity"
                >
                  Mehr
                  <i className={`fa-solid fa-chevron-down text-xs transition-transform ${internalDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {internalDropdownOpen && (
                  <div className="absolute top-full left-0 mt-xs bg-white rounded-lg shadow-xlarge border border-neutral py-xs min-w-[200px] z-50">
                    {moreLinks.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="flex items-center gap-sm py-xs px-md font-archivo text-sm text-content-primary hover:bg-surface-canvas-3 transition-colors"
                        onClick={() => setInternalDropdownOpen(false)}
                      >
                        <i className={`fa-solid ${link.icon} w-4 text-content-brand`} />
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Right Side Icons */}
          <div className="hidden lg:flex items-center gap-lg">
            {/* Language Selector */}
            <div className="relative" ref={languageDropdownRef}>
              <button
                onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                className="p-xs hover:opacity-70 transition-opacity text-content-brand"
                aria-label="Select language"
              >
                <i className="fa-solid fa-globe text-xl" />
              </button>

              {languageDropdownOpen && (
                <div className="absolute top-full right-0 mt-xs bg-white rounded-lg shadow-xlarge border border-neutral py-xs min-w-[120px] z-50">
                  {locales.map((l) => (
                    <Link
                      key={l}
                      href={`/${l}`}
                      className={`block px-md py-xs font-archivo text-sm transition-colors ${
                        locale === l
                          ? 'text-content-brand bg-surface-canvas-3'
                          : 'text-content-primary hover:bg-surface-canvas-3'
                      }`}
                      onClick={() => setLanguageDropdownOpen(false)}
                    >
                      {l === 'de' ? 'Deutsch' : l === 'en' ? 'English' : l.toUpperCase()}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Notifications — internal staff only */}
            {user && hasInternalAccess && (
              <NotificationBell locale={locale} align="right" />
            )}

            {/* Profile */}
            <Link
              href={user ? `/${locale}/konto` : `/${locale}/auth/login`}
              className="p-xs hover:opacity-70 transition-opacity text-content-brand"
              aria-label={user ? 'My Account' : 'Login'}
            >
              <i className="fa-regular fa-user text-xl" />
            </Link>

            {/* Logout Button - only show when logged in */}
            {user && (
              <form action={`/${locale}/auth/logout`} method="POST">
                <button
                  type="submit"
                  className="p-xs hover:opacity-70 transition-opacity text-content-brand"
                  aria-label="Logout"
                  title="Logout"
                >
                  <i className="fa-solid fa-right-from-bracket text-xl" />
                </button>
              </form>
            )}

          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-xs"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H21M3 12H21M3 18H21" stroke="#133324" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-surface-canvas-3 z-50 lg:hidden overflow-y-auto">
          <div className="flex flex-col min-h-full">
            {/* Mobile Header */}
            <div className="flex items-center justify-between py-md px-lg">
              <Link href={`/${locale}`} onClick={() => setMobileMenuOpen(false)}>
                <span className="font-faro text-2xl font-bold text-content-brand">futalis</span>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-xs text-content-brand"
                aria-label="Close menu"
              >
                <i className="fa-solid fa-xmark text-2xl" />
              </button>
            </div>

            {/* Mobile Navigation - Employee modules */}
            <nav className="flex-1 px-lg py-xl">
              <ul className="space-y-lg">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="flex items-center gap-sm font-faro text-[28px] leading-tight text-content-brand hover:text-content-primary transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <i className={`fa-solid ${link.icon} w-8 text-interactive-primary`} />
                      {link.label}
                    </Link>
                  </li>
                ))}

                {/* More modules */}
                {moreLinks.length > 0 && (
                  <li className="pt-md border-t border-content-brand/20">
                    <span className="block font-archivo text-sm text-content-tertiary mb-sm uppercase tracking-wider">
                      Weitere Module
                    </span>
                    <ul className="space-y-sm">
                      {moreLinks.map((link) => (
                        <li key={link.href}>
                          <Link
                            href={link.href}
                            className="flex items-center gap-sm font-archivo text-lg text-content-brand hover:text-content-primary transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <i className={`fa-solid ${link.icon} w-5 text-interactive-primary`} />
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
              </ul>
            </nav>

            {/* Mobile Bottom Actions */}
            <div className="px-lg py-xl border-t border-content-brand/20">
              <div className="flex items-center justify-between">
                {/* Language */}
                <div className="flex items-center gap-sm">
                  <i className="fa-solid fa-globe text-xl text-content-brand" />
                  <div className="flex gap-xs">
                    {locales.map((l) => (
                      <Link
                        key={l}
                        href={`/${l}`}
                        className={`font-archivo text-sm px-xs py-2xs rounded ${
                          locale === l
                            ? 'text-interactive-primary font-semibold'
                            : 'text-content-brand hover:text-content-primary'
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {l.toUpperCase()}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Profile */}
                <Link
                  href={user ? `/${locale}/konto` : `/${locale}/auth/login`}
                  className="p-xs text-content-brand"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="fa-regular fa-user text-xl" />
                </Link>
              </div>

              {/* Login/Logout */}
              <div className="mt-lg">
                {user ? (
                  <div className="flex items-center justify-between">
                    <span className="font-archivo text-sm text-content-tertiary">
                      {user.email}
                    </span>
                    <form action={`/${locale}/auth/logout`} method="POST">
                      <button
                        type="submit"
                        className="font-archivo text-sm text-system-error-2 hover:underline"
                      >
                        Logout
                      </button>
                    </form>
                  </div>
                ) : (
                  <Link
                    href={`/${locale}/auth/login`}
                    className="block w-full py-sm bg-interactive-primary text-content-on-primary font-archivo font-semibold text-center rounded-lg hover:bg-interactive-primary-hover transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
